import { Section } from '../../models';
import { insightsPanelPrintable } from './insights-review-notice';
import { DEHYDRATION_PANEL_ENABLED } from './dehydration-insights';

/**
 * Every section that has an Insights Panel, in report order.
 *
 * The same set printsInsightsPanel (pdf-section.tsx) can answer yes for — its
 * `case` labels, listed. That switch stays the authority on whether a panel
 * prints for a PARTICULAR test, because each section asks its own question of
 * the analysis; this list answers the section-level one, which three places need
 * and none of them can ask a switch:
 *
 *   - the section header, for whether to offer "Include Insights Panel in PDF";
 *   - Report Settings, for the checkboxes that seed it; and
 *   - the drift test, which asserts no OTHER section prints a panel.
 *
 * A tiny module of its own rather than a const in pdf-section.tsx, which pulls
 * in the whole reference-range chart stack: Report Settings needs the list and
 * nothing else from there.
 */
export const INSIGHTS_PANEL_SECTIONS: Section[] = [
  Section.RBC,
  Section.IRON,
  Section.WBC,
  Section.GLUCOSE,
  Section.NUTRIENTS,
  Section.ELECTROLYTES,
  Section.THYROID,
  Section.STRESS_ADRENAL,
  Section.LIPIDS,
];

/**
 * Whether this build prints `section`'s Insights Panel for any test at all.
 *
 * Membership in the list above says the section HAS a panel, which is a
 * different question: three of those are pending clinical review and print
 * nowhere yet (insightsPanelPrintable), and Hydration additionally waits on
 * DEHYDRATION_PANEL_ENABLED.
 *
 * This is the gate on OFFERING the "Include Insights Panel in PDF" choice, in
 * the section header and in Report Settings. A checkbox over a panel that
 * cannot print is a control with no effect, and the practitioner has no way to
 * tell that from a broken one. Each panel picks its checkbox up on the day it
 * ships, because shipping it is what changes this answer.
 *
 * Deliberately NOT a per-test question: the header asks that separately (it has
 * the panel in hand and can see whether it drew anything), and Report Settings
 * has no test to ask about.
 */
export const insightsPanelPrintsInThisBuild = (section: Section): boolean =>
  INSIGHTS_PANEL_SECTIONS.includes(section) &&
  insightsPanelPrintable(section) &&
  (section !== Section.ELECTROLYTES || DEHYDRATION_PANEL_ENABLED);
